import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { CSSObject, styled, Theme } from '@mui/material/styles';

import {
  ArrowDownIcon,
  HomeIcon,
  LogOutLineIcon,
  Notification2FillIcon,
  MapIcon,
  UserGroupIcon,
  UserMultipleIcon,
  Folder2Icon,
  PieChartIcon,
  MapActiveIcon,
  DashboardSquareIcon,
  UserMultipleActiveIcon,
  PieChartActiveIcon,
  Folder2ActiveIcon,
  ArrowRightIcon,
  ResetPasswordIcon,
  UserGroup2Icon,
  Alert4Active,
  Alert4Icon,
} from '~/assets/icons';

import classNames from 'classnames/bind';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagVi from '~/assets/images/flag-vi.png';
import FlagEn from '~/assets/images/flag-en.png';
import DefaultImage from '~/assets/images/default.png';
import { BASE_COLORS } from '~/utils/constants/common';
import styles from './Main.module.scss';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import _ from 'lodash';
import { adminRouteAbsolute } from '~/utils/constants/route';
import Notification from '~/pages/admin/dashboard/components/Notification';
import MuiDrawer from '@mui/material/Drawer';
import { handleGetAuthUserProfile, handleLogout } from '~/thunks/auth/authThunk';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import BaseAutocomplete from '~/components/common/base-autocomplete/BaseAutocomplete';
import userApi from '~/apis/user';
import { cast, handleApiError } from '~/utils/helper';
import { commonActions } from '~/thunks/common/commonSlice';
import { IUser } from '~/utils/interface/user';
import { authActions } from '~/thunks/auth/authSlice';
import { LanguageEnum, StorageEnum } from '~/utils/enum';
import { locationActions } from '~/thunks/location/locationSlice';
import { userActions } from '~/thunks/user/userSlice';
import { personActions } from '~/thunks/person/personSlice';

type AppBarProps = MuiAppBarProps & {
  open?: boolean;
  location?: any;
};

type TSubMenu = {
  name: string;
  url: string;
  permission?: string;
};

type TSidebar = {
  id: string;
  name: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  url: string;
  subMenu?: Array<TSubMenu>;
};

type TSubMenuOpen = {
  [field: string]: boolean;
};

const sidebarList = [
  {
    id: 'dashboard',
    name: 'dashboard',
    icon: <HomeIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    activeIcon: <DashboardSquareIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    url: adminRouteAbsolute.dashboard,
  },
  {
    id: 'map',
    name: 'map',
    icon: <MapIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    activeIcon: <MapActiveIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    url: adminRouteAbsolute.map,
  },
  {
    id: 'person',
    name: 'person',
    icon: <UserMultipleIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    activeIcon: <UserMultipleActiveIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    url: '/admin/persons',
  },
  {
    id: 'alert',
    name: 'alert',
    icon: <Alert4Icon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    activeIcon: <Alert4Active className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    url: adminRouteAbsolute.alerts,
  },
  {
    id: 'management',
    name: 'management',
    icon: <Folder2Icon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    activeIcon: <Folder2ActiveIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    url: '',
    subMenu: [
      {
        name: 'account',
        url: '/admin/users',
      },
      {
        name: 'location',
        url: '/admin/locations',
      },
    ],
  },
  {
    id: 'statistic',
    name: 'statistic',
    icon: <PieChartIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    activeIcon: <PieChartActiveIcon className='lg:w-5 lg:h-5 xl:w-6 xl:h-6' />,
    url: '',
    subMenu: [
      {
        name: 'reportAggregate',
        url: adminRouteAbsolute.reports,
      },
      {
        name: 'reportViolation',
        url: adminRouteAbsolute.reportsViolations,
      },
      {
        name: 'reportJourney',
        url: adminRouteAbsolute.reportsJourney,
      },
    ],
  },
];

const cx = classNames.bind(styles);

const drawerWidth = 250;
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open, location }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `${
      location.pathname === '/admin/profile' ||
      location.pathname === '/admin/change-password' ||
      location.pathname === '/admin/action-logs'
        ? ''
        : `calc(100% - ${drawerWidth}px)`
    }`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export default function MiniDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { t, i18n } = useTranslation(['admin/layout', 'admin/common']);

  const { isLoading } = useAppSelector((state) => state.common);
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);

  const [open] = useState<boolean | undefined>(true);
  const [activeScreen, setActiveScreen] = useState<string>('dashboard');
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [anchorElement, setAnchorElement] = React.useState<HTMLButtonElement | null>(null);

  const [isOpenLogoutDialog, setIsOpenLogoutDialog] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<TSubMenuOpen>({});

  const [anchorLang, setAnchorLang] = useState<null | HTMLElement>(null);

  const openUser = Boolean(anchorEl);
  const id = openUser ? 'simple-popover' : undefined;

  const openNotification = Boolean(anchorElement);
  const idNotification = openNotification ? 'simple-popover' : undefined;

  const openLang = Boolean(anchorLang);
  const idLang = openLang ? 'popover-lang' : undefined;

  const currentLang = i18n.language;

  const profileRoutes = ['/admin/profile', '/admin/change-password', '/admin/action-logs'];
  const isUserProfile = profileRoutes.includes(location.pathname);

  const [userListOptions, setUserListOptions] = useState<Array<IUser>>([]);

  useEffect(() => {
    const stored = localStorage.getItem(StorageEnum.SELECTED_USER);
    if (stored) {
      const user = JSON.parse(stored);
      if (user && Object.keys(user).length > 0) {
        dispatch(authActions.setSelectedUser(user));
        dispatch(handleGetAuthUserProfile({ username: user.username }));
        return;
      }
    }

    dispatch(handleGetAuthUserProfile({ username: cast(userProfile?.username) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (selectedUser) {
      localStorage.setItem(StorageEnum.SELECTED_USER, JSON.stringify(selectedUser));
    } else {
      localStorage.removeItem(StorageEnum.SELECTED_USER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  useEffect(() => {
    let matchedUrl = location.pathname;

    setActiveScreen(matchedUrl);
  }, [location]);

  const handleClickNotification = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNotification = () => {
    setAnchorElement(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const permissionSidebar: Array<TSidebar> = useMemo(() => {
    const permissionItems: Array<TSidebar> = [];
    sidebarList.forEach((sidebar) => {
      const permissionItem: TSidebar = _.clone(sidebar);
      let hasPermission: boolean = true;

      if (hasPermission) permissionItems.push(permissionItem);
    });
    return permissionItems;
  }, []);

  const handleClickAccountInfo = () => {
    navigate(adminRouteAbsolute.profile);
    handleClose();
  };

  const handleClickChangePassword = () => {
    navigate(adminRouteAbsolute.changePassword);
    handleClose();
  };

  const handleNavigateDrawer = (url: string) => {
    navigate(url);
  };

  const handleOpenLang = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorLang(event.currentTarget);
  };

  const handleCloseLang = () => {
    setAnchorLang(null);
  };

  const handleToggleLanguage = () => {
    const newLang = i18n.language === LanguageEnum.VI ? LanguageEnum.EN : LanguageEnum.VI;
    i18n.changeLanguage(newLang);

    handleCloseLang();
  };

  const handleClickLogout = () => {
    setIsOpenLogoutDialog(true);
    handleClose();
  };

  const handleConfirmLogout = async () => {
    try {
      dispatch(authActions.resetAuthState());
      localStorage.removeItem(StorageEnum.SELECTED_USER);
      await dispatch(handleLogout());
      navigate(adminRouteAbsolute.login, { replace: true });
      window.history.pushState(null, '', adminRouteAbsolute.login);
    } catch (e: any) {}
  };

  const renderSidebar = () => {
    if (isUserProfile) return t('sidebar.accountInfo');

    for (const item of sidebarList) {
      const path = location.pathname;
      const activeSub = item.subMenu?.find((sub) => path.startsWith(sub.url));

      if (activeSub) return t(`sidebar.${activeSub.name}`);

      // If no submenu then get parent
      const isActive = isMenuActive(item);
      if (isActive) return t(`sidebar.${item.name}`);
    }
  };

  const handleSearchUserList = async (searchKey: string) => {
    dispatch(commonActions.setIsLoading(true));
    try {
      const response = await userApi.getList({
        username: userProfile?.username,
        searchusername: searchKey,
        issearch: true,
      });

      setUserListOptions(response.data.result.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  const handleSelect = (selectedOption: IUser | null) => {
    if (selectedOption) {
      dispatch(userActions.resetFilter());
      dispatch(personActions.resetFilter());
      dispatch(locationActions.resetFilter());

      dispatch(authActions.setSelectedUser(selectedOption));
      localStorage.setItem(StorageEnum.SELECTED_USER, JSON.stringify(selectedOption));
      dispatch(handleGetAuthUserProfile({ username: selectedOption.username }));
      return;
    }

    dispatch(authActions.setSelectedUser({}));
    localStorage.removeItem(StorageEnum.SELECTED_USER);
    dispatch(handleGetAuthUserProfile({}));
  };

  const profileAvatarSrc = Object.values(selectedUser).length > 0 ? selectedUser : userProfile;

  const isMenuActive = (item: TSidebar) => {
    const path = location.pathname;

    const isParent = item.url && path.startsWith(item.url);

    const isSub = item.subMenu?.some((sub) => path.startsWith(sub.url));

    return isParent || isSub;
  };

  return (
    <Box className='w-full h-full'>
      <Box className={cx('layout-main')}>
        {isLoading && <Box className='h-full w-full z-[100000] opacity-0 cursor-wait absolute top-0 left-0'></Box>}

        {/* Header */}
        <AppBar position='fixed' open={open} location={location} color='default'>
          <Box
            className={cx('flex justify-between items-center px-[30px] lg:gap-[16px] xl:gap-[24px] h-[80px]', {
              'pl-0 not-open': !open,
            })}
          >
            <Typography className='font-bold lg:text-2xl xl:text-[20px] text-[#161A1F] flex-1'>
              {renderSidebar()}
            </Typography>
            <Box className='w-[360px]'>
              <BaseAutocomplete
                value={selectedUser}
                onSearch={handleSearchUserList}
                onSelected={handleSelect}
                options={userListOptions}
                iconStart={Object.values(selectedUser).length > 0 ? <UserGroup2Icon /> : <UserGroupIcon />}
              />
            </Box>

            <Box className='flex flex-row gap-2 cursor-pointer' onClick={handleOpenLang}>
              <Avatar className='w-6 h-6' alt={currentLang} src={currentLang === LanguageEnum.VI ? FlagVi : FlagEn} />
              <Box className='justify-between hidden text-right sm:flex items-center'>
                <Box className='text-base text-[#161A1F]'>
                  {currentLang === LanguageEnum.VI ? t(LanguageEnum.VI) : t(LanguageEnum.EN)}
                </Box>
              </Box>
              <Box className='flex items-center'>
                <IconButton className='flex justify-center p-0'>
                  <ArrowDownIcon
                    stroke='#161A1F'
                    className={cx('transition-transform duration-300', {
                      'rotate-180': openLang,
                    })}
                  />
                </IconButton>
              </Box>
            </Box>
            <Box>
              <IconButton className={cx('badge-button')} onClick={handleClickNotification}>
                <Badge color='info'>
                  <Notification2FillIcon />
                </Badge>
              </IconButton>
              <IconButton aria-describedby={id} size='large' onClick={handleClick}>
                <Avatar className='w-7 h-7' alt={'Avatar'} src={profileAvatarSrc?.image ?? DefaultImage} />
              </IconButton>
            </Box>
          </Box>

          <Popover
            id={id}
            open={openUser}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: '25px',
                  width: '280px',
                  borderRadius: '12px',
                  boxShadow: '5px 4px 10px 3px #00000040',
                },
              },
            }}
            sx={{ zIndex: 9999 }}
          >
            <Box>
              <Box
                className='w-full h-[38px] flex justify-between items-center px-6 hover:cursor-pointer hover:bg-slate-100'
                onClick={handleClickAccountInfo}
              >
                <Avatar className='w-7 h-7 mr-4' alt={'Avatar'} src={profileAvatarSrc?.image ?? DefaultImage} />
                <Typography className='text-[#161A1F] text-base font-medium mr-auto'>
                  {t('header.accountInfo')}
                </Typography>
                <ArrowRightIcon className='w-2' stroke='#7C8796' />
              </Box>
              <Box
                className='w-full h-[38px] flex justify-between items-center px-6 hover:cursor-pointer hover:bg-slate-100'
                onClick={handleClickChangePassword}
              >
                <ResetPasswordIcon className='mr-6' />
                <Typography className='text-[#161A1F] text-base font-medium mr-auto'>
                  {t('header.changePassword')}
                </Typography>
                <ArrowRightIcon className='w-2' stroke='#7C8796' />
              </Box>
              <Box
                className='w-full h-[38px] flex justify-between items-center px-6 hover:cursor-pointer hover:bg-slate-100'
                onClick={handleClickLogout}
              >
                <LogOutLineIcon className='mr-6' />
                <Typography className='text-[#161A1F] text-base font-medium mr-auto'>{t('header.logout')}</Typography>
                <ArrowRightIcon className='w-2' stroke='#7C8796' />
              </Box>
            </Box>
          </Popover>
          <Popover
            id={idNotification}
            open={openNotification}
            anchorEl={anchorElement}
            onClose={handleCloseNotification}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: '25px',
                  width: '425px',
                  borderRadius: '12px',
                },
              },
            }}
          >
            <Notification />
          </Popover>
          <Popover
            id={idLang}
            open={openLang}
            anchorEl={anchorLang}
            onClose={handleCloseLang}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: '25px',
                  width: '200px',
                  borderRadius: '12px',
                  padding: '10px 20px',
                },
              },
            }}
          >
            <Box className='flex flex-row gap-2 cursor-pointer' onClick={handleToggleLanguage}>
              {currentLang === LanguageEnum.VI ? (
                <>
                  <Avatar className='w-6 h-6' alt={LanguageEnum.EN} src={FlagEn} />
                  <Box className='justify-between hidden text-right sm:flex items-center'>
                    <Box className={`${BASE_COLORS.COLOR_TEXT_PRIMARY} text-base`}>{t(LanguageEnum.EN)}</Box>
                  </Box>
                </>
              ) : (
                <>
                  <Avatar className='w-6 h-6' alt={LanguageEnum.VI} src={FlagVi} />
                  <Box className='justify-between hidden text-right sm:flex items-center'>
                    <Box className={`${BASE_COLORS.COLOR_TEXT_PRIMARY} text-base`}>{t(LanguageEnum.VI)}</Box>
                  </Box>
                </>
              )}
            </Box>
          </Popover>
        </AppBar>

        {/* Sidebar */}
        {!isUserProfile && (
          <Drawer variant='permanent' open={open} className='sidebar'>
            <DrawerHeader className='h-20 flex justify-center items-center'>
              {/* <BaseImage src={Logo} className='h-20' alt='Logo' /> */}
            </DrawerHeader>

            <List>
              {permissionSidebar.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    className={cx({
                      active: isMenuActive(item),
                    })}
                    disablePadding
                    onClick={() => {
                      if (item?.subMenu) {
                        const currentMenuOpen = { ...menuOpen };
                        currentMenuOpen[item.name] = !currentMenuOpen[item.name];
                        setMenuOpen(currentMenuOpen);
                        return;
                      }
                      setActiveScreen(item.name);
                      item.url && handleNavigateDrawer(item.url);
                    }}
                  >
                    <ListItemButton className={`flex justify-between items-center`}>
                      <Box className='flex items-center'>
                        <ListItemIcon>{isMenuActive(item) ? item.activeIcon : item.icon}</ListItemIcon>
                        <ListItemText primary={t(`sidebar.${item.name}`)} sx={{ opacity: open ? 1 : 0 }} />
                      </Box>

                      {item?.subMenu && open && (
                        <ArrowDownIcon
                          stroke={`${isMenuActive(item) ? '#ffffff' : '#7C8796'}`}
                          className={cx('transition-all duration-200', {
                            'rotate-180': menuOpen[item.name],
                          })}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                  {item?.subMenu?.length && open && (
                    <Collapse in={menuOpen[item.name]} timeout='auto' key={index} unmountOnExit>
                      <List component='div' disablePadding className='sub-menu'>
                        {item.subMenu.map((subMenu: TSubMenu, subMenuIndex: number) => (
                          <ListItem
                            key={subMenuIndex}
                            className={cx('sub-menu-item', {
                              active: location.pathname.startsWith(subMenu.url),
                            })}
                            onClick={() => handleNavigateDrawer(subMenu.url)}
                          >
                            <ListItemButton
                              className={cx({
                                'sub-menu-item-label-ellipsis':
                                  subMenu.name === 'reportJourney' && activeScreen !== subMenu.url,
                                'sub-menu-item-label-wrap':
                                  subMenu.name === 'reportJourney' && activeScreen === subMenu.url,
                              })}
                            >
                              <ListItemText primary={t(`sidebar.${subMenu.name}`)} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Drawer>
        )}

        {/* Main content */}
        <Box component='main' className={isUserProfile ? cx('main-profile') : cx('main')}>
          <Outlet />
        </Box>
      </Box>

      <ConfirmDialog
        open={isOpenLogoutDialog}
        title={t('notification', { ns: 'admin/common' })}
        description={t('logout.description')}
        cancel={t('close', { ns: 'admin/common' })}
        confirm={t('logout.title')}
        onConfirm={handleConfirmLogout}
        onCancel={() => setIsOpenLogoutDialog(false)}
      />
    </Box>
  );
}
